import { useCallback, useState } from 'react';

/**
 * NumericKeys<T>
 * Derives a union of keys from T whose values are numbers.
 * e.g. { a: number; b: string; c: number } -> "a" | "c"
 */
type NumericKeys<T> = {
  [K in keyof T]: T[K] extends number ? K : never;
}[keyof T];

/**
 * InitialLoading<T>
 * - boolean: apply the same default loading state to ALL keys
 * - Partial<Record<keyof T, boolean>>: per-key defaults
 */
type InitialLoading<T> = boolean | Partial<Record<keyof T, boolean>>;

/**
 * useNumericRecord
 * Generic hook to manage:
 *  - a numeric record (e.g., dashboard totals)
 *  - a parallel per-key loading map
 *
 * Provides helpers:
 *  - merge: batch update multiple numeric fields
 *  - set: set a single numeric field
 *  - inc: increment a numeric field by N (default 1)
 *  - setLoading: toggle loading for one key
 *  - setAllLoading: toggle loading for all keys
 *  - withAsync: wrap an async operation for a single key (auto-set/unset loading)
 */
export function useNumericRecord<T extends Record<string, number>>(
  initial: T,
  initialLoading?: InitialLoading<T>
) {
  // Numeric values (single source of truth for the record)
  const [state, setState] = useState<T>(initial);

  // Per-key loading flags, initialized from `initialLoading`
  const [loading, setLoadingState] = useState<Record<keyof T, boolean>>(() => {
    const base = {} as Record<keyof T, boolean>;
    // Initialize every key that exists in `initial`
    (Object.keys(initial) as Array<keyof T>).forEach((k) => {
      base[k] =
        typeof initialLoading === 'boolean'
          ? initialLoading // same default for all keys
          : !!initialLoading?.[k]; // per-key default or false
    });
    return base;
  });

  /**
   * Merge multiple numeric fields at once.
   * Uses functional setState to avoid stale closures.
   */
  const merge = useCallback((patch: Partial<T>) => {
    setState((s) => ({ ...s, ...patch }));
  }, []);

  /**
   * Set a single numeric field to an exact value.
   */
  const set = useCallback(<K extends keyof T>(key: K, value: T[K]) => {
    setState((s) => ({ ...s, [key]: value }));
  }, []);

  /**
   * Increment a numeric field by `by` (default 1).
   * Restricted to numeric keys via NumericKeys<T>.
   */
  const inc = useCallback(<K extends NumericKeys<T>>(key: K, by = 1) => {
    setState((s) => ({ ...s, [key]: (s[key] as number) + by }));
  }, []);

  /**
   * Set loading for a single key.
   */
  const setLoading = useCallback(<K extends keyof T>(key: K, value: boolean) => {
    setLoadingState((l) => ({ ...l, [key]: value }));
  }, []);

  /**
   * Set loading for ALL keys at once.
   */
  const setAllLoading = useCallback((value: boolean) => {
    setLoadingState((l) => {
      const next = { ...l };
      (Object.keys(l) as Array<keyof T>).forEach((k) => (next[k] = value));
      return next;
    });
  }, []);

  /**
   * withAsync
   * Helper to run an async operation for a single key:
   *  - sets loading[key] = true
   *  - awaits the promise
   *  - writes the resulting number to state[key]
   *  - sets loading[key] = false (finally)
   *
   * Accepts a promise that resolves to either a number or { value: number }.
   */
  const withAsync = useCallback(
    async <K extends keyof T>(key: K, run: () => Promise<number> | Promise<{ value: number }>) => {
      setLoading(key, true);
      try {
        const result = await run();
        const value = typeof result === 'number' ? result : result.value;
        set(key, value as T[K]);
        return value;
      } finally {
        setLoading(key, false);
      }
    },
    [set, setLoading]
  );

  return { state, loading, merge, set, inc, setLoading, setAllLoading, withAsync };
}
